import { useState, useMemo, useEffect, useCallback } from 'react';
import { useTypedSelector } from "../../../store/reducers/use-typed-selector";
import Paging from '../../general-ui/paging/paging';
import Search from '../../general-ui/search-bar/search';
import TextInput from "../../general-ui/text-input/text";
import AccessRow from './access-row';
import Modal from '../../general-ui/modal/modal';
import { BLAdmin, PERMISSIONS } from '../../../types/working-model';
import { isValidEmail } from '../../../utils/utils';
import { useDispatch } from 'react-redux';
import { addAdmin, setWorkingPermissions } from '../../../store/actions/admin/bl-admin';
import ReactSelect from 'react-select';
import { reactSelectStyles } from '../../general-ui/react-select/react-select-styles';
import './channel-admins.scss';


const ChannelAdmins = ({ overview }: { overview?: true; }): JSX.Element => {
	const { admins } = useTypedSelector(state => state.ChannelAdminReducer);
	const { token, user } = useTypedSelector(state => state.AuthReducer);

	const [inviteModalOpen, setInviteModalOpen] = useState(false);
	const [inviteEmail, setInviteEmail] = useState("");
	const [invitePermissions, setInvitePermissions] = useState([]);
	const [inviteError, setInviteError] = useState("");
	const [permissionsError, setPermissionsError] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [search, setSearch] = useState("");

	const dispatch = useDispatch();

	// set and store permissions state for current user
	// this might be something to implement on login so state can be used for permissions throughout site
	useEffect(() => {
		for (let i = 0; i < admins.length; i++) {
			if (admins[i].id === user?.id) { dispatch(setWorkingPermissions(admins[i].channels[user?.active_channel])); }
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user, token, admins]);

	const handleInviteModal = () => {
		setInviteError("");
		setPermissionsError("");
		setInviteModalOpen(true);
	};

	function handleInvite() {
		if (isValidEmail(inviteEmail)) {
			if (!invitePermissions.length) {
				setPermissionsError("Please select permissions for new admin");
			} else {
				dispatch(addAdmin(token!, inviteEmail, invitePermissions));
				setInviteModalOpen(false);
				setInviteEmail("");
				setInvitePermissions([]);
			}
		} else {
			setInviteError("Invalid email entered");
		}
		return;
	}

	function handleChange(value: any) {
		if (permissionsError)
			setPermissionsError("");
		const array = value.map((value: { label: string, value: string }) => {
			return value.value;
		});
		setInvitePermissions(array);
	}

	const handleSearch = (searchEntry: string): void => {
		setCurrentPage(1);
		setSearch(searchEntry);
	};

	const filteredAdmins = useMemo((): BLAdmin[] => {
		// search only looks at email address, may consider including name and permissions
		return admins.filter((admin) => admin.email.toLowerCase().includes(search.toLowerCase()));
	}, [admins, search]);

	const displayAdmins = useMemo((): JSX.Element => {
		const currentAdmins: BLAdmin[] =
			overview ? filteredAdmins.slice(0, 5) : filteredAdmins.slice((currentPage - 1) * 8, currentPage * 8);
		setTotalPages(Math.ceil(filteredAdmins.length / 8));

		return (
			<div className="admins-list">
				{currentAdmins.length ? (
					currentAdmins.map((profile: BLAdmin) => {
						return (
							<AccessRow
								key={profile.id}
								profile={profile}
							/>
						);
					})
				) : (
					<div>No results found</div>
				)}
			</div>);
	}, [filteredAdmins, currentPage, overview]);

	const closeInviteModal = useCallback(() => {
		setInviteModalOpen(false);
	}, []);

	return (
		<div className="channel-admins-container">
			{!overview && (
				<div className="search-row">
					<Search
						onClear={() => setSearch("")}
						placeholder={"Search members"}
						searchOnDebounce
						onDebounce={e => handleSearch(e)}
					/>
					<button onClick={handleInviteModal} className="primary invite-button">
						Invite&nbsp;admin
					</button>
				</div>
			)}
			{displayAdmins}
			{!overview && totalPages > 1 && (
				<div className="pagination">
					<Paging
						page={currentPage}
						setPage={setCurrentPage}
						totalPages={totalPages}
					/>
				</div>
			)}

			{/* need design comp for this modal, need style for errors, email error should disappear when email is corrected */}
			<Modal
				open={inviteModalOpen}
				title={'Invite by email'}
				onRequestClose={closeInviteModal}
				cancellable={true}
				closeable={false}
				footer={(
					<>
						<button onClick={closeInviteModal}>Cancel</button>
						<button className="primary" onClick={() => handleInvite()}>Invite</button>
					</>
				)}
			>
				<>
					<div className="invite-details-container">
						<div className='invite-email'>
							<TextInput
								label={"Email"}
								placeholder={""}
								defaultValue={""}
								value={inviteEmail}
								onChange={(event) => setInviteEmail(event.target.value)}
							/>
							<div className="email-error">{inviteError}</div>
						</div>
						<div className="invite-permissions">
							<label>Permissions</label>
							<ReactSelect
								styles={reactSelectStyles({
									menu: {
										right: 0,
										top: 0
									},
									menuList: {
										fontSize: '14px'
									},
									container: {
										display: 'flex',
										alignItems: 'center'
									},
									control: {
										borderRadius: 100,
										border: '1px solid #908CA0',
										padding: '4px',
										fontSize: '14px'
									},
									multiValue: {
										backgroundColor: 'transparent'
									},
									multiValueLabel: {
										color: '#fff',
									}
								})}
								isMulti={true}
								options={Object.entries(PERMISSIONS).map(([k, v]) => ({ label: k, value: v }))}
								onChange={(value) => handleChange(value)}
								isClearable={false}
								menuPortalTarget={document.body}
							/>
							<div className="permissions-error">{permissionsError}</div>
						</div>
					</div>
				</>
			</Modal>
		</div>
	);
};

export default ChannelAdmins;